import classNames from "classnames";
import SVGIcon from "components/v2/Images/SVGIcon";
import Input from "components/v2/Input";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import { getMeOrganizations } from "../../../../../store/organization/action";
import {
  selectFilteredOrganizations,
  selectOranizations,
  selectTotalFilteredOrganizations,
} from "../../../../../store/organization/selectors";
import { getMeWorkspaces } from "../../../../../store/workspace/action";
import { selectFilteredWorkspaces, selectTotalFilteredWorkspaces } from "../../../../../store/workspace/selectors";
import styles from "./style.module.css";

const PAGE_SIZE = 4;

const EntityListViewPanel = ({
  onCancel,
  filteredWorkspaces,
  entityId,
  onEntityRowClick,
  getMeWorkspaces,
  getMeOrganizations,
  filteredOrganizations,
  totalFilteredOrganizations,
  totalFilteredWorkspaces,
  defaultFilteredOrganizationId,
  organizations,
}) => {
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredOrganizationId, setFilteredOrganizationId] = useState(defaultFilteredOrganizationId || null);

  const [organizationsPage, setOrganizationsPage] = useState(1);
  const [workspacesPage, setWorkspacesPage] = useState(1);

  const isLoadMoreOrganizationVisible = useMemo(() => {
    return !filteredOrganizationId && totalFilteredOrganizations > filteredOrganizations.length;
  }, [filteredOrganizationId, totalFilteredOrganizations, filteredOrganizations.length]);

  const isLoadMoreWorkspacesVisible = useMemo(() => {
    return totalFilteredWorkspaces > filteredWorkspaces.length;
  }, [totalFilteredWorkspaces, filteredWorkspaces]);

  const debouncedOnSearchChange = useMemo(() => {
    return debounce((value) => {
      setOrganizationsPage(1);
      setWorkspacesPage(1);
      setSearchQuery(value);
    }, 500);
  }, []);

  const handleOnSearchChange = useCallback(
    (e) => {
      setSearchValue(e.target.value);
      debouncedOnSearchChange(e.target.value);
    },
    [debouncedOnSearchChange],
  );

  const fetchMeOrganizations = useCallback(
    async ({ searchQuery, page }) => {
      try {
        const payloadParams = {
          ...(searchQuery ? { searchQuery } : {}),
          ...(page ? { page } : {}),
          ...(filteredOrganizationId ? { organizationIds: filteredOrganizationId } : {}),
          pageSize: PAGE_SIZE,
        };

        await getMeOrganizations({ payload: { params: payloadParams } });
      } catch (error) {
        console.error(error);
      }
    },
    [filteredOrganizationId, getMeOrganizations],
  );

  const fetchMeWorkspaces = useCallback(
    async ({ organizationIds, searchQuery, page }) => {
      try {
        setIsLoadingWorkspaces(true);

        const payloadParams = {
          ...(organizationIds ? { organizationIds } : {}),
          ...(searchQuery ? { searchQuery } : {}),
          ...(page ? { page } : {}),
          pageSize: PAGE_SIZE,
        };

        await getMeWorkspaces({ payload: { params: payloadParams } });
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingWorkspaces(false);
      }
    },
    [getMeWorkspaces],
  );

  const handleOrganizationFilterButtonClick = useCallback(
    (e, organizationId) => {
      e.preventDefault();
      e.stopPropagation();
      if (filteredOrganizationId) {
        setFilteredOrganizationId(null);
        setOrganizationsPage(1);
        setWorkspacesPage(1);

        return;
      }
      setWorkspacesPage(1);
      setFilteredOrganizationId(organizationId);
    },
    [filteredOrganizationId],
  );

  const handleResetFilter = useCallback(() => {
    setFilteredOrganizationId(null);
    setOrganizationsPage(1);
    setWorkspacesPage(1);
  }, []);

  const handleLoadMoreOrganizations = useCallback(() => {
    setOrganizationsPage((prev) => prev + 1);
  }, []);

  const handleLoadMoreWorkspaces = useCallback(() => {
    setWorkspacesPage((prev) => prev + 1);
  }, []);

  const finalFilteredOrganizations = useMemo(() => {
    if (filteredOrganizationId) {
      return filteredOrganizations.filter((org) => org.organizationId === filteredOrganizationId);
    }

    return filteredOrganizations;
  }, [filteredOrganizations, filteredOrganizationId]);

  const showWorkspacesBlock = Boolean(filteredOrganizationId) || isLoadingWorkspaces || filteredWorkspaces.length > 0;

  const filteredOrganizationDisplayName = useMemo(() => {
    return organizations.find((org) => org.organizationId === filteredOrganizationId)?.displayName;
  }, [organizations, filteredOrganizationId]);

  useEffect(() => {
    fetchMeOrganizations({ searchQuery, page: organizationsPage });
  }, [fetchMeOrganizations, searchQuery, organizationsPage]);

  useEffect(() => {
    fetchMeWorkspaces({ searchQuery, organizationIds: filteredOrganizationId, page: workspacesPage });
  }, [fetchMeWorkspaces, searchQuery, filteredOrganizationId, workspacesPage]);

  return (
    <div className={styles.entitySwitchPanelRoot}>
      <div className={styles.switchPanelHeader}>
        <div className={styles.switchPanelTitle}>Switch to</div>
        <button type="button" className={styles.switchPanelCancel} onClick={onCancel}>
          Cancel
        </button>
      </div>
      <div className={styles.switchPanelSearchSection}>
        <div className={styles.searchWrapper}>
          <SVGIcon src="components/v2/Filters/SearchBar/search.svg" className={styles.searchLeadingIcon} alt="" />
          <Input
            placeholder="Search"
            value={searchValue}
            onChange={handleOnSearchChange}
            inputWrapperClassName={styles.search}
            inputClassName={styles.searchInput}
          />
        </div>
      </div>
      <div className={styles.popoverScrollableContent}>
        {filteredOrganizationId ? (
          <div className={styles.filteredOrganizationDisplayName}>
            {filteredOrganizationDisplayName}
            <SVGIcon
              src="components/v2/Sidebar/BottomNavigationBar/close.svg"
              className={styles.filteredOrganizationDisplayNameIcon}
              onClick={handleResetFilter}
            />
          </div>
        ) : null}
        {finalFilteredOrganizations?.length > 0 ? (
          <>
            <div className={styles.sectionHeaderWithCount}>
              {filteredOrganizationId ? "Organization" : `Organizations (${totalFilteredOrganizations})`}
            </div>
            <ul className={styles.entityRowList}>
              {finalFilteredOrganizations?.map((org) => (
                <li key={org.organizationId} className={styles.entityRowListItem}>
                  <div
                    tabIndex={0}
                    data-entity-id={org.organizationId}
                    aria-label={`Switch to organization ${org.displayName}`}
                    data-testid={
                      entityId === org?.organizationId ? "BottomNavigationBar-active-organization" : org.organizationId
                    }
                    onClick={() => onEntityRowClick(org.organizationId)}
                    className={classNames(styles.entityRow, {
                      [styles.entityRowActive]: entityId === org?.organizationId,
                    })}
                  >
                    <SVGIcon
                      src="components/v2/Sidebar/BottomNavigationBar/organization.svg"
                      className={styles.entityRowLeadingIcon}
                      alt=""
                    />
                    <div>
                      <span className={styles.entityRowLabel}>{org.displayName}</span>
                    </div>
                    {!filteredOrganizationId ? (
                      <button
                        type="button"
                        className={classNames(styles.entityRowFilterButton, {
                          [styles.entityRowFilterButtonActive]: filteredOrganizationId === org.organizationId,
                        })}
                        aria-label={`Filter workspaces by ${org.displayName}`}
                        aria-pressed={filteredOrganizationId === org.organizationId}
                        onClick={(e) => handleOrganizationFilterButtonClick(e, org.organizationId)}
                      >
                        <SVGIcon
                          src="components/v2/Filters/SearchBar/filter.svg"
                          className={styles.entityRowTrailingIcon}
                          alt=""
                        />
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {isLoadMoreOrganizationVisible ? (
          <div onClick={handleLoadMoreOrganizations} className={styles.loadMoreButton}>
            Load More
          </div>
        ) : null}
        {showWorkspacesBlock ? (
          <>
            <div className={styles.sectionHeaderWithCount}>Workspaces ({totalFilteredWorkspaces})</div>
            {isLoadingWorkspaces ? (
              <div className={styles.filterWorkspacesLoading}>Loading workspaces…</div>
            ) : filteredWorkspaces?.length > 0 ? (
              <ul className={styles.entityRowList}>
                {filteredWorkspaces?.map((workspace) => (
                  <li key={workspace.workspaceId} className={styles.entityRowListItem}>
                    <div
                      role="button"
                      tabIndex={0}
                      data-entity-id={workspace.workspaceId}
                      aria-label={`Switch to workspace ${workspace.displayName}`}
                      data-testid={
                        entityId === workspace?.workspaceId
                          ? "BottomNavigationBar-active-workspace"
                          : workspace.workspaceId
                      }
                      onClick={() => onEntityRowClick(workspace.workspaceId)}
                      className={classNames(styles.entityRow, styles.entityRowWorkspace, {
                        [styles.entityRowActive]: entityId === workspace?.workspaceId,
                      })}
                    >
                      <SVGIcon
                        src="components/v2/Sidebar/BottomNavigationBar/workspace.svg"
                        className={styles.entityRowLeadingIcon}
                        alt=""
                      />
                      <span className={styles.entityRowLabel}>{workspace.displayName}</span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : filteredOrganizationId ? (
              <div className={styles.switchPanelEmpty}>No workspaces found for this organization.</div>
            ) : null}
            {isLoadMoreWorkspacesVisible ? (
              <div onClick={handleLoadMoreWorkspaces} className={styles.loadMoreButton}>
                Load More
              </div>
            ) : null}
          </>
        ) : null}
        {finalFilteredOrganizations?.length === 0 &&
        filteredWorkspaces?.length === 0 &&
        !isLoadingWorkspaces &&
        !filteredOrganizationId ? (
          <div className={styles.switchPanelEmpty}>No organizations or workspaces match your search.</div>
        ) : null}
      </div>
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  filteredWorkspaces: selectFilteredWorkspaces,
  filteredOrganizations: selectFilteredOrganizations,
  organizations: selectOranizations,
  totalFilteredOrganizations: selectTotalFilteredOrganizations,
  totalFilteredWorkspaces: selectTotalFilteredWorkspaces,
});

const mapDispatchToProps = {
  getMeWorkspaces,
  getMeOrganizations,
};

export default connect(mapStateToProps, mapDispatchToProps)(EntityListViewPanel);
